import { useMemo, useState } from "react";
import { BOUNDARY_RADIUS, CENTER, SLICE_ANGLE, TOTAL_SLICES } from "./constants";
import { RADIAL_BANDS } from "./radialBands";
import {
  generateRegions, generateCoarseRegions,
} from "./regionGenerator";
import { resolvePosition } from "./positionResolver";
import { polarToCartesian } from "./geometry";

// 30-yard circle radius. Aligned with generateCoarseRegions()'s
// ring/deep band split at outer-of-band-2 = 24 so the visual circle
// matches where the 16-zone numbering divides ring from deep.
const THIRTY_YARD_RADIUS = 24;

// Convert a (x, y) viewBox click into a (sliceIndex, bandIndex) pair on the
// underlying 48 x 6 grid. Used by the coarse 8-wedge mode so the recorded
// fielding position reflects WHERE in the wedge the operator clicked
// (inner-ring vs deep) rather than the wedge's representative center.
function pointToSliceBand(x, y) {
  const dx = x - CENTER.x;
  const dy = y - CENTER.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  let angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI - 90;
  while (angleDeg < 0) angleDeg += 360;
  while (angleDeg >= 360) angleDeg -= 360;
  const sliceIndex = Math.floor(angleDeg / SLICE_ANGLE) % TOTAL_SLICES;
  let bandIndex = RADIAL_BANDS[RADIAL_BANDS.length - 1].bandIndex;
  for (const b of RADIAL_BANDS) {
    if (dist >= b.innerRadius && dist < b.outerRadius) {
      bandIndex = b.bandIndex;
      break;
    }
  }
  return { sliceIndex, bandIndex, distance: dist };
}

// Compact label for coarse-mode rendering — keeps SVG text from overflowing
// the wedge. Shortens common multi-word fielding positions to 2-3 letter
// abbreviations cricket scorers already recognise.
function shortenPositionLabel(name) {
  if (!name) return "";
  const map = {
    "Mid Off": "Mid Off",
    "Mid On": "Mid On",
    "Long Off": "Long Off",
    "Long On": "Long On",
    "Cover": "Cover",
    "Extra Cover": "Cover+",
    "Deep Cover": "Deep Cov",
    "Deep Extra Cover": "Deep Cov+",
    "Sweeper Cover": "Sweeper",
    "Point": "Point",
    "Backward Point": "BW Point",
    "Deep Point": "Deep Pt",
    "Third Man": "Third Man",
    "Fine Third": "Fine 3rd",
    "Fine Third Man": "Fine 3rd",
    "Slip": "Slip",
    "Wicket Keeper": "Keeper",
    "Leg Slip": "Leg Slip",
    "Square Leg": "Sq Leg",
    "Backward Square Leg": "BW Sq Leg",
    "Deep Square Leg": "Deep Sq",
    "Deep Backward Square Leg": "Deep BW Sq",
    "Fine Leg": "Fine Leg",
    "Deep Fine Leg": "Deep Fine",
    "Very Fine Leg": "V.Fine Leg",
    "Mid Wicket": "MidWkt",
    "Deep Mid Wicket": "Deep MW",
    "Deep Mid On": "Deep MO",
    "Bowler": "Bowler",
    "Straight": "Straight",
  };
  return map[name] || name;
}

function getHeatColor(intensity) {
  if (intensity == null) return "transparent";
  //const clamped = Math.max(0, Math.min(1, intensity));
  const clamped = 0;
  return `rgba(0, 200, 83, ${0.15 + clamped * 0.55})`;
}

function getPositionColor(positionName) {
  if (!positionName) return "#80AF81";
  return "#1A5319";
  const pos = positionName.toLowerCase();

  if (
    pos.includes("bowler") ||
    pos.includes("wicket keeper") ||
    pos.includes("long stop")
  ) {
    return "#1A5319";
  }
  if (pos.includes("mid off") || pos.includes("mid on")) {
    return "#80AF81";
  }
  if (pos.includes("long off") || pos.includes("long on")) {
    return "#1A5319";
  }
  if (pos.includes("extra cover") || pos.includes("cover")) {
    return "#508D4E";
  }
  if (pos.includes("deep cover") || pos.includes("sweeper")) {
    return "#1A5319";
  }
  if (pos.includes("point")) {
    return "#80AF81";
  }
  if (
    pos.includes("slip") ||
    pos.includes("gully") ||
    pos.includes("third man") ||
    pos.includes("short third")
  ) {
    return "#508D4E";
  }
  if (pos.includes("fine")) {
    return "#80AF81";
  }
  if (pos.includes("square leg") || pos.includes("backward square")) {
    return "#508D4E";
  }
  if (pos.includes("deep mid wicket")) {
    return "#1A5319";
  }
  if (pos.includes("mid wicket") || pos.includes("mid-wicket")) {
    return "#508D4E";
  }
  if (pos.includes("silly")) {
    return "#D6EFD8";
  }

  return "#80AF81";
}

export default function CricketFieldEngine({
  batterHand = "RIGHT",
  selectedRegionId,
  onRegionSelect,
  heatmapData = {},
  showRegionLabels = false,
  coarseInputMode = true,
  // Exact-pin support: when (pinX, pinY) are set, a precise marker is rendered
  // at that viewBox coordinate. Both fields stay in 0..100 space.
  pinX = null,
  pinY = null,
  // When true, an outer annulus (just past the boundary rope) becomes
  // clickable so the operator can mark exactly where a boundary 4/6
  // crossed the rope. Inner wedges are dimmed since no fielder applies.
  isBoundary = false,
}) {
  const regions = useMemo(
    // 2026-05-27 founder ask — switch coarse mode from 8 full-radius
    // wedges to the 16-zone layout (8 sectors × ring/deep) so each
    // zone gets a distinct number badge for voice "wagon N" input.
    () => (coarseInputMode ? generateCoarseRegions() : generateRegions()),
    [coarseInputMode],
  );
  // In coarse-input mode, default labels ON since there are only 16 chunky zones
  const labelsEnabled = showRegionLabels || coarseInputMode;
  const [hoveredRegionId, setHoveredRegionId] = useState(null);

  // Convert a DOM mouse event into SVG viewBox coords (0..100 for both axes).
  // Returns null if the SVG context can't be resolved.
  const eventToSvgPoint = (event) => {
    const svg = event.currentTarget?.ownerSVGElement || event.currentTarget;
    if (!svg || typeof svg.createSVGPoint !== "function") return null;
    const pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  };

  // Bowler: slice 0, band 2 → angle 0 = bottom of field
  const bowlerPos = polarToCartesian(0, 20);
  // Keeper: directly behind batter → angle 180 = top of field
  const keeperPos = polarToCartesian(180, 6);

  const selectedRegion = useMemo(() => {
    if (!selectedRegionId) return null;

    if (selectedRegionId === "bowler") {
      return {
        regionId: "bowler",
        center: bowlerPos,
        position: "Bowler",
        sliceIndex: 0,
        bandIndex: 2,
      };
    }
    if (selectedRegionId === "wicket-keeper") {
      return {
        regionId: "wicket-keeper",
        center: keeperPos,
        position: "Wicket Keeper",
        sliceIndex: 24,
        bandIndex: 0,
      };
    }

    const region = regions.find((r) => r.regionId === selectedRegionId);
    if (region) {
      const midAngle = (region.startAngle + region.endAngle) / 2;
      const midRadius = (region.innerRadius + region.outerRadius) / 2;
      return {
        ...region,
        center: polarToCartesian(midAngle, midRadius),
        position: resolvePosition(
          region.sliceIndex,
          region.bandIndex,
          batterHand,
        ),
      };
    }

    // Fallback: a saved ball in coarse-input mode may carry a regionId from
    // the original 48×6 grid that doesn't match any of the 24 macro zones.
    // Parse the regionId, compute its center, and render the indicator so the
    // EDIT-ball view still shows where the ball went.
    const m = /^(\d+)-(\d+)$/.exec(selectedRegionId);
    if (!m) return null;
    const sliceIndex = parseInt(m[1], 10);
    const bandIndex = parseInt(m[2], 10);
    const band = RADIAL_BANDS[bandIndex];
    if (!band) return null;
    const midAngle = sliceIndex * SLICE_ANGLE + SLICE_ANGLE / 2;
    const midRadius = (band.innerRadius + band.outerRadius) / 2;
    return {
      regionId: selectedRegionId,
      sliceIndex,
      bandIndex,
      center: polarToCartesian(midAngle, midRadius),
      position: resolvePosition(sliceIndex, bandIndex, batterHand),
    };
  }, [batterHand, bowlerPos, keeperPos, regions, selectedRegionId]);

  const baseFill = "#80AF81";
  const selectedFill = "rgba(255, 255, 0, 0.7)";

  return (
    // viewBox expanded from `0 0 100 100` to `-12 -12 124 124` (2026-05-18)
    // so the outer-ring boundary labels (Square Leg / Cover / Point /
    // Third Man) — which sit at viewBox r≈52 from centre (50,50) — have
    // room to render. With the old viewBox the labels at x=-2 / x=102
    // were clipped against the SVG edges. All inner coordinates (pitch,
    // stumps, wedges, boundary circle) still use absolute 0..100 coords
    // centred at (50,50); the larger viewBox just adds breathing room
    // around them. The wheel renders slightly smaller in the same
    // container — the right trade to fully reveal every label.
    <svg viewBox="-12 -12 124 124" className="w-full h-full">
      <defs>
        <filter id="segGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="1.2"
            floodColor="#ffff00"
            floodOpacity="0.9"
          />
        </filter>
      </defs>
      {/* Field background + boundary */}
      <circle
        cx={CENTER.x}
        cy={CENTER.y}
        r={BOUNDARY_RADIUS}
        fill="#1A5319"
        stroke="#D6EFD8"
        strokeWidth="0.4"
      />
      {/* Pitch + crease + stumps + batter marker — visual chrome only.
          pointerEvents="none" so clicks land on the wedge underneath
          rather than being swallowed by the chrome. */}
      <g style={{ pointerEvents: "none" }}>
        <rect
          x="47.5"
          y="32.5"
          width="5"
          height="18.333"
          rx="0.5"
          fill="#c8a96e"
          opacity="0.9"
        />
        <line x1="46.333" y1="45"     x2="53.667" y2="45"     stroke="white" strokeWidth="0.3" />
        <line x1="46.333" y1="38.333" x2="53.667" y2="38.333" stroke="white" strokeWidth="0.3" />
        <line x1="49.167" y1="44.667" x2="49.167" y2="46.333" stroke="#333"  strokeWidth="0.4" />
        <line x1="50"     y1="44.667" x2="50"     y2="46.333" stroke="#333"  strokeWidth="0.4" />
        <line x1="50.833" y1="44.667" x2="50.833" y2="46.333" stroke="#333"  strokeWidth="0.4" />
        <line x1="49.167" y1="37"     x2="49.167" y2="38.667" stroke="#333"  strokeWidth="0.4" />
        <line x1="50"     y1="37"     x2="50"     y2="38.667" stroke="#333"  strokeWidth="0.4" />
        <line x1="50.833" y1="37"     x2="50.833" y2="38.667" stroke="#333"  strokeWidth="0.4" />
        <circle cx="50" cy="45.667" r="0.833" fill="#e74c3c" stroke="white" strokeWidth="0.3" />
      </g>

      {/* All field regions */}
      {regions.map((r) => {
        // Coarse mode: 16 zones, label each by its proper position
        // (Mid Off vs Long Off, Cover vs Deep Cover, etc.) via the
        // canonical resolver. universalRegionLabel was only useful when
        // we had 8 full-radius wedges; now each zone has a distinct
        // semantic.
        const positionName = resolvePosition(r.sliceIndex, r.bandIndex, batterHand);
        const intensity = heatmapData[r.regionId];
        const isSelected = selectedRegionId === r.regionId;
        const midAngle = (r.startAngle + r.endAngle) / 2;
        // Push the label out toward the boundary so it sits clear of the pitch.
        const labelRadiusBias = coarseInputMode ? 0.55 : 0.5;
        const midRadius = r.innerRadius + (r.outerRadius - r.innerRadius) * labelRadiusBias;
        const labelPos = polarToCartesian(midAngle, midRadius);
        // Coarse mode: alternate shading by both sector AND band-kind so
        // adjacent ring/deep zones in the same sector are visually
        // distinct as well as adjacent sectors.
        const coarseBaseColor = coarseInputMode
          ? ((r.sectorIndex + (r.bandKind === "deep" ? 1 : 0)) % 2 === 0
              ? "#508D4E" : "#1A5319")
          : null;
        const baseColor = coarseBaseColor || getPositionColor(positionName);
        const isHovered = hoveredRegionId === r.regionId;

        return (
          <g key={r.regionId}>
            <path
              d={r.path}
              fill={
                // The selected wedge no longer gets a yellow tint — the
                // crosshair pin (rendered below) is the only selection
                // indicator the operator needs. Hover still highlights.
                intensity != null
                  ? getHeatColor(intensity)
                  : baseColor || baseFill
              }
              stroke={isHovered ? "#ffff00" : coarseInputMode ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.35)"}
              strokeWidth={isHovered ? "0.6" : coarseInputMode ? "0.4" : "0.2"}
              filter={isHovered ? "url(#segGlow)" : "none"}
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setHoveredRegionId(r.regionId)}
              onMouseLeave={() => setHoveredRegionId(null)}
              onClick={(event) => {
                const point = eventToSvgPoint(event);
                // Coarse 8-wedge mode: resolve the precise fielding position
                // from the click coords so inside-30 vs deep clicks map to
                // their proper position labels (Mid Off vs Long Off, Cover vs
                // Deep Cover, etc.). Fine mode keeps the wedge representative.
                let outSlice = r.sliceIndex;
                let outBand = r.bandIndex;
                let outPosition = positionName;
                if (coarseInputMode && point) {
                  const sb = pointToSliceBand(point.x, point.y);
                  outSlice = sb.sliceIndex;
                  outBand = sb.bandIndex;
                  outPosition = resolvePosition(sb.sliceIndex, sb.bandIndex, batterHand);
                }
                onRegionSelect?.({
                  regionId: r.regionId,
                  sliceIndex: outSlice,
                  bandIndex: outBand,
                  position: outPosition,
                  x: point?.x ?? null,
                  y: point?.y ?? null,
                });
              }}
            />
            {labelsEnabled && (
              <text
                x={labelPos.x}
                y={labelPos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={coarseInputMode ? "2.4" : "1.8"}
                fontWeight={coarseInputMode ? "600" : "400"}
                fill="rgba(255,255,255,0.9)"
                style={{ pointerEvents: "none" }}
              >
                {coarseInputMode ? positionName : r.regionId}
              </text>
            )}
            {/* Cell-by-number badge: rendered near the OUTER edge of
                each of the 16 zones (8 sectors × ring/deep) so the
                scorer can say "wagon 5" and pick that exact zone.
                Numbering: 1 = sector 0 ring (Mid Off), 2 = sector 0
                deep (Long Off), ... 15 = sector 7 ring, 16 = sector 7
                deep. Founder ask 2026-05-27. */}
            {coarseInputMode && r.cellNumber != null && (() => {
              // Place badge at ~85% radius into the wedge so ring badges
              // sit just inside the 30-yard circle and deep badges sit
              // near the boundary — leaves the position label centred.
              const badgeR = r.innerRadius + (r.outerRadius - r.innerRadius) * 0.85;
              const badgePos = polarToCartesian(midAngle, badgeR);
              return (
                <g style={{ pointerEvents: "none" }}>
                  <circle
                    cx={badgePos.x}
                    cy={badgePos.y}
                    r="2"
                    fill="rgba(0,0,0,0.6)"
                    stroke="rgba(255,255,255,0.6)"
                    strokeWidth="0.25"
                  />
                  <text
                    x={badgePos.x}
                    y={badgePos.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize="2.2"
                    fontWeight="700"
                    fill="#ffffff"
                  >
                    {r.cellNumber}
                  </text>
                </g>
              );
            })()}
          </g>
        );
      })}

      {/* 30-yard circle — drawn AFTER wedges so it's clearly visible on top
          as the inner-ring / deep boundary. Solid white stroke for clarity. */}
      <circle
        cx={CENTER.x}
        cy={CENTER.y}
        r={THIRTY_YARD_RADIUS}
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="0.45"
        strokeOpacity="0.85"
        strokeDasharray="1.2,0.6"
        style={{ pointerEvents: "none" }}
      />

      {/* Outside-the-boundary annulus — only renders for boundary balls.
          8 sectors aligned with the wedges so clicking a "six over deep
          mid wicket" lands on the right region in viewBox coords. The
          wagon_wheel_x/y pin is what's saved; the position label stays
          the wedge's deep-most label (e.g. "Deep Mid Wicket"). */}
      {isBoundary && coarseInputMode && (() => {
        const OUTER_R = BOUNDARY_RADIUS + 8; // r=56 — well inside viewBox 100
        const SECTOR_COUNT = 8;
        const SECTOR_DEG = 360 / SECTOR_COUNT;
        return (
          <g>
            {Array.from({ length: SECTOR_COUNT }).map((_, sectorIdx) => {
              const startDeg = sectorIdx * SECTOR_DEG;
              const endDeg = (sectorIdx + 1) * SECTOR_DEG;
              const inner = BOUNDARY_RADIUS;
              const outer = OUTER_R;
              // Build an annulus-arc path
              const p1 = polarToCartesian(startDeg, inner);
              const p2 = polarToCartesian(endDeg, inner);
              const p3 = polarToCartesian(endDeg, outer);
              const p4 = polarToCartesian(startDeg, outer);
              const path = [
                `M ${p1.x} ${p1.y}`,
                `A ${inner} ${inner} 0 0 1 ${p2.x} ${p2.y}`,
                `L ${p3.x} ${p3.y}`,
                `A ${outer} ${outer} 0 0 0 ${p4.x} ${p4.y}`,
                "Z",
              ].join(" ");
              // The deep zone this segment sits against. `regions` holds two
              // zones per sector (ring, deep), so regions[sectorIdx] read the
              // wrong sector — "Covers" straight, "Point" at fine leg.
              const matchingWedge = regions.find(
                (r) => r.sectorIndex === sectorIdx && r.bandKind === "deep",
              );
              const label = matchingWedge
                ? resolvePosition(matchingWedge.sliceIndex, matchingWedge.bandIndex, batterHand)
                : "";
              const midAngle = (startDeg + endDeg) / 2;
              const labelPos = polarToCartesian(midAngle, inner + (outer - inner) * 0.5);
              return (
                <g key={`outer-${sectorIdx}`}>
                  <path
                    d={path}
                    fill="rgba(255, 215, 0, 0.18)"
                    stroke="rgba(255, 215, 0, 0.6)"
                    strokeWidth="0.4"
                    strokeDasharray="0.8,0.4"
                    style={{ cursor: "pointer" }}
                    onClick={(event) => {
                      const point = eventToSvgPoint(event);
                      // Inherit slice/band from the wedge so position label
                      // is correct (Deep Mid Wicket, Long On, etc.); store
                      // exact click coords so the pin renders outside the
                      // rope where the ball actually crossed.
                      let outSlice = matchingWedge?.sliceIndex ?? 0;
                      let outBand = matchingWedge?.bandIndex ?? 5;
                      let outPosition = label;
                      if (point) {
                        const sb = pointToSliceBand(point.x, point.y);
                        outSlice = sb.sliceIndex;
                        outBand = sb.bandIndex; // resolver clamps to deep band
                        outPosition = resolvePosition(sb.sliceIndex, Math.min(sb.bandIndex, 5), batterHand);
                      }
                      onRegionSelect?.({
                        regionId: matchingWedge?.regionId ?? `${outSlice}-${outBand}`,
                        sliceIndex: outSlice,
                        bandIndex: Math.min(outBand, 5),
                        position: outPosition,
                        x: point?.x ?? null,
                        y: point?.y ?? null,
                      });
                    }}
                  />
                  <text
                    x={labelPos.x}
                    y={labelPos.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="2.0"
                    fontWeight="600"
                    fill="rgba(255, 215, 0, 0.9)"
                    style={{ pointerEvents: "none" }}
                  >
                    {label}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })()}

      {/* Selection indicator
          Priority: exact pin (pinX/pinY) > zone centroid fallback.
          The pin is the user's actual click location; the centroid is the
          legacy "where did the zone live" fallback for old balls without coords. */}
      {(() => {
        const hasPin = pinX != null && pinY != null;
        const target = hasPin
          ? { x: pinX, y: pinY }
          : selectedRegion?.center || null;
        if (!target) return null;
        return (
          <>
            <line
              x1={CENTER.x}
              y1={CENTER.y}
              x2={target.x}
              y2={target.y}
              stroke="rgba(255, 0, 0, 0.65)"
              strokeWidth="0.5"
              strokeDasharray={hasPin ? "0" : "1.5,1"}
              style={{ pointerEvents: "none" }}
            />
            {hasPin ? (
              // Crosshair pin — larger ring + center dot so the precise location
              // reads cleanly even on a small SVG
              <g style={{ pointerEvents: "none" }}>
                <circle
                  cx={target.x}
                  cy={target.y}
                  r="2.2"
                  fill="none"
                  stroke="rgba(255,255,255,0.95)"
                  strokeWidth="0.5"
                />
                <circle
                  cx={target.x}
                  cy={target.y}
                  r="1.0"
                  fill="rgba(239, 68, 68, 0.95)"
                  stroke="rgba(255,255,255,0.95)"
                  strokeWidth="0.3"
                />
              </g>
            ) : (
              <circle
                cx={target.x}
                cy={target.y}
                r="1.4"
                fill="rgba(255, 0, 0, 0.7)"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth="0.2"
                style={{ pointerEvents: "none" }}
              />
            )}
          </>
        );
      })()}

      {/* Bowler marker */}
      <circle
        cx={bowlerPos.x}
        cy={bowlerPos.y}
        r="2.2"
        fill={
          selectedRegionId === "bowler"
            ? selectedFill
            : "rgba(231, 76, 60, 0.75)"
        }
        stroke={selectedRegionId === "bowler" ? "#ffff00" : "rgba(0,0,0,0.4)"}
        style={{ cursor: "pointer" }}
        onClick={() =>
          onRegionSelect?.({
            regionId: "bowler",
            sliceIndex: 0,
            bandIndex: 2,
            position: "Bowler",
          })
        }
      />

      {/* Wicket Keeper marker */}
      <circle
        cx={keeperPos.x}
        cy={keeperPos.y}
        r="2.2"
        fill={
          selectedRegionId === "wicket-keeper"
            ? selectedFill
            : "rgba(231, 76, 60, 0.75)"
        }
        stroke={
          selectedRegionId === "wicket-keeper"
            ? "#ffff00"
            : "rgba(0,0,0,0.4)"
        }
        style={{ cursor: "pointer" }}
        onClick={() =>
          onRegionSelect?.({
            regionId: "wicket-keeper",
            sliceIndex: 24,
            bandIndex: 0,
            position: "Wicket Keeper",
          })
        }
      />
    </svg>
  );
}
